import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import Map, { Marker, Popup } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import Config from '@config'
import { useLanguage } from '@context'
import { instagramMapStyle } from '../mapStyle'

const MIN_ZOOM = 1
const MAX_ZOOM = 18

const WebMap = forwardRef(
  ({ center, zoom, style, contacts, onContactClick, rightOffset = 16 }: any, ref) => {
    const { t } = useLanguage()
    const mapRef = useRef<any>(null)
    const [hoveredContactId, setHoveredContactId] = useState<string | null>(null)
    const [currentZoom, setCurrentZoom] = useState<number>(zoom ?? 6)

    React.useEffect(() => {
      mapRef.current?.flyTo({
        center: [center[1], center[0]],
        zoom: zoom ?? 6,
        duration: 1500,
      })
    }, [center, zoom])

    // Keep the slider in sync with native gestures (scroll / pinch) without
    // re-creating the handler on every render of the map.
    const onZoom = useCallback((e: any) => setCurrentZoom(e.viewState.zoom), [])

    // Drive the map zoom from the right-hand slider / +- buttons.
    const applyZoom = useCallback((next: number, duration = 0) => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next))
      setCurrentZoom(clamped)
      mapRef.current?.zoomTo(clamped, { duration })
    }, [])

    const zoomToContact = useCallback((contact: any) => {
      mapRef.current?.flyTo({
        center: [contact.lng, contact.lat],
        zoom: 15,
        duration: 1000,
      })
    }, [])

    useImperativeHandle(ref, () => ({ zoomToContact }), [zoomToContact])

    const hoveredContact = hoveredContactId
      ? contacts.find((c: any) => c.id === hoveredContactId)
      : null

    return (
      <div style={{ ...style, position: 'relative' }}>
        <Map
          ref={mapRef}
          initialViewState={{
            latitude: center[0],
            longitude: center[1],
            zoom: zoom ?? 6,
          }}
          mapboxAccessToken={Config.MAPBOX_ACCESS_TOKEN}
          mapStyle={JSON.parse(instagramMapStyle)}
          projection="globe"
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onZoom={onZoom}
          style={{ width: '100%', height: '100%' }}
        >
          {contacts.map((contact: any) => (
            <Marker
              key={contact.id}
              latitude={contact.lat}
              longitude={contact.lng}
              anchor="center"
              onClick={() => onContactClick(contact.id)}
            >
              <div
                onMouseEnter={() => setHoveredContactId(contact.id)}
                onMouseLeave={() => setHoveredContactId(null)}
                style={{ position: 'relative', cursor: 'pointer' }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    // Ghost markers get a purple ring to stand out from real contacts.
                    border: contact.isGhost ? '3px solid #9C27B0' : '3px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    backgroundImage: contact.avatarUrl ? `url('${contact.avatarUrl}')` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                {contact.isGhost && (
                  <div
                    style={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: 'white',
                      border: '1px solid #9C27B0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      lineHeight: '14px',
                    }}
                  >
                    👻
                  </div>
                )}
              </div>
            </Marker>
          ))}

          {hoveredContact && (
            <Popup
              latitude={hoveredContact.lat}
              longitude={hoveredContact.lng}
              anchor="bottom"
              offset={20}
              closeButton={false}
              closeOnClick={false}
            >
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <strong>{hoveredContact.fullName}</strong>
                {hoveredContact.profession?.name && (
                  <>
                    <br />
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {hoveredContact.profession.name}
                    </span>
                  </>
                )}
              </div>
            </Popup>
          )}
        </Map>

        {/* Top horizontal zoom slider ( - / drag / + ), centered over the
          visible map (the panel area on the right is excluded via rightOffset). */}
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: rightOffset,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              background: 'white',
              borderRadius: 22,
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
              pointerEvents: 'auto',
              userSelect: 'none',
            }}
          >
            <button
              type="button"
              aria-label={t('mapScreen.zoomOut')}
              onClick={() => applyZoom(currentZoom - 1, 250)}
              style={zoomBtnStyle}
            >
              -
            </button>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.1}
              value={currentZoom}
              aria-label={t('mapScreen.zoomLevel')}
              onChange={(e) => applyZoom(Number(e.target.value), 0)}
              style={{ width: 200, cursor: 'pointer', accentColor: '#1b3a6b' }}
            />
            <button
              type="button"
              aria-label={t('mapScreen.zoomIn')}
              onClick={() => applyZoom(currentZoom + 1, 250)}
              style={zoomBtnStyle}
            >
              +
            </button>
          </div>
        </div>
      </div>
    )
  },
)

const zoomBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 15,
  border: 'none',
  background: '#1b3a6b',
  color: 'white',
  fontSize: 18,
  lineHeight: '30px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export default WebMap
